import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, Plus, X, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getInventoryRoleMappings, replaceInventoryRoleMappings } from '../api';

const INVENTORY_ROLES = [
  { value: 'state_head', label: 'State Head' },
  { value: 'asm',        label: 'ASM (Area Sales Manager)' },
  { value: 'tl',         label: 'Team Lead' },
  { value: 'fse',        label: 'Field Sales Executive' },
];

/**
 * Inventory Role Mapping dialog.
 *
 * Lets HR explicitly map arbitrary HRMS designations → inventory role
 * tokens (state_head / asm / tl / fse). Explicit mappings override the
 * regex-based keyword fallback baked into the backend, so custom titles
 * like "Zonal Sales Champion" can be routed to State Head without a
 * code change.
 *
 * State machine:
 *   - `mappings` is the working copy that reflects unsaved edits.
 *   - "Save" replaces the entire server-side mapping set (idempotent PUT).
 *   - Client-side dedup on (designation_id, inventory_role) so HR can't
 *     accidentally double-map the same designation to the same role.
 */
const InventoryRoleMappingDialog = ({ open, onOpenChange, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [designationSearch, setDesignationSearch] = useState('');
  const [newDesignationId, setNewDesignationId] = useState('');
  const [newRole, setNewRole] = useState('state_head');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getInventoryRoleMappings();
      setDesignations(res.data.designations || []);
      setMappings(res.data.mappings || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load role mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
      setDesignationSearch('');
      setNewDesignationId('');
      setNewRole('state_head');
    }
  }, [open]);

  const filteredDesignations = useMemo(() => {
    if (!designationSearch.trim()) return designations;
    const q = designationSearch.toLowerCase();
    return designations.filter(d => (d.title || '').toLowerCase().includes(q));
  }, [designations, designationSearch]);

  const designationTitle = (id) => designations.find(d => d.id === id)?.title || '(unknown)';

  const addMapping = () => {
    if (!newDesignationId) return;
    // Client-side dedup: don't re-add the same (designation_id, role) pair
    if (mappings.some(m => m.designation_id === newDesignationId && m.inventory_role === newRole)) {
      toast.warning('That designation is already mapped to that role');
      return;
    }
    setMappings([...mappings, { designation_id: newDesignationId, inventory_role: newRole }]);
    setNewDesignationId('');
    setDesignationSearch('');
  };

  const removeMapping = (idx) => {
    setMappings(mappings.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = mappings.map(m => ({ designation_id: m.designation_id, inventory_role: m.inventory_role }));
      const res = await replaceInventoryRoleMappings(payload);
      toast.success(`Saved ${res.data.count} mapping${res.data.count === 1 ? '' : 's'}`);
      if (typeof onSaved === 'function') onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  // Group mappings by role for the display
  const mappingsByRole = useMemo(() => {
    const groups = {};
    for (const m of mappings) {
      (groups[m.inventory_role] ||= []).push(m);
    }
    return groups;
  }, [mappings]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="role-mapping-dialog">
        <DialogHeader>
          <DialogTitle>Inventory Role Mapping</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Explicitly map HRMS designations to inventory-role dropdowns (State Head, ASM, Team Lead, FSE). Explicit mappings override the keyword-based fallback used by the transfer/issue recipient picker.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        )}

        {!loading && (
          <>
            {/* Add-new row */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="mapping-add-row">
              <div className="text-xs font-semibold text-slate-700">Add mapping</div>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-7">
                  <Input
                    placeholder="Search designation…"
                    value={designationSearch}
                    onChange={(e) => setDesignationSearch(e.target.value)}
                    className="h-8 text-xs mb-1"
                    data-testid="mapping-desig-search"
                  />
                  <Select value={newDesignationId} onValueChange={setNewDesignationId}>
                    <SelectTrigger className="h-8 text-xs" data-testid="mapping-desig-select">
                      <SelectValue placeholder="Choose designation…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {filteredDesignations.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">No matches</div>
                      )}
                      {filteredDesignations.slice(0, 100).map((d) => (
                        <SelectItem key={d.id} value={d.id} className="text-xs">
                          {d.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <div className="h-8 text-[10px] text-slate-500 mb-1 flex items-center">Maps to</div>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger className="h-8 text-xs" data-testid="mapping-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVENTORY_ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex items-end">
                  <Button
                    size="sm"
                    className="w-full h-8 text-xs"
                    onClick={addMapping}
                    disabled={!newDesignationId}
                    data-testid="mapping-add-btn"
                  >
                    <Plus size={12} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Current mappings, grouped by role */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {INVENTORY_ROLES.map(role => {
                const rows = mappingsByRole[role.value] || [];
                return (
                  <div
                    key={role.value}
                    className="rounded-md border border-slate-200 p-2.5"
                    data-testid={`mapping-group-${role.value}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Badge className="text-[10px] py-0 px-1.5 bg-indigo-100 text-indigo-700 border-indigo-300">
                          {role.label}
                        </Badge>
                        <span className="text-[11px] text-slate-500">
                          {rows.length} mapping{rows.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {rows.length === 0 && (
                        <span className="text-[10px] text-slate-400 italic">
                          Falls back to keyword patterns
                        </span>
                      )}
                    </div>
                    {rows.length > 0 && (
                      <div className="space-y-1">
                        {rows.map((m) => {
                          const globalIdx = mappings.indexOf(m);
                          return (
                            <div
                              key={`${m.designation_id}-${m.inventory_role}`}
                              className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1"
                            >
                              <span className="text-slate-700 truncate">
                                {designationTitle(m.designation_id)}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeMapping(globalIdx)}
                                className="text-red-500 hover:text-red-700 p-0.5"
                                title="Remove mapping"
                                data-testid={`mapping-remove-${globalIdx}`}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="mt-2 gap-2">
          {mappings.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-red-600 hover:bg-red-50 mr-auto"
              onClick={() => setMappings([])}
              data-testid="mapping-clear-all-btn"
            >
              <Trash2 size={12} className="mr-1" /> Clear all
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mapping-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="mapping-save-btn">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
            Save mappings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryRoleMappingDialog;
